import {
  CampaignApiType,
  CampaignAudienceType,
  CampaignEventType,
  CampaignNotificationPlan,
} from './CampaignApiDto';
import {
  CampaignExperimentalType,
  CampaignExperimentalStatus,
  AssignmentType,
  NotificationChannel,
  AutomationTriggerEvent,
} from './CampaignExperimentalDto';

/**
 * Date formatting utilities
 */
export const DateUtils = {
  formatDateOnly: (dateString: string): string => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD format
  },

  formatTimeWithSeconds: (timeString: string): string => {
    if (timeString.includes(':') && timeString.split(':').length === 3) {
      return timeString;
    }
    if (timeString.includes(':') && timeString.split(':').length === 2) {
      return `${timeString}:00`;
    }
    return timeString;
  },

  isDateInFuture: (dateString: string): boolean => {
    return new Date(dateString) > new Date();
  },

  isDateInPast: (dateString: string): boolean => {
    return new Date(dateString) < new Date();
  },
};

/**
 * Type mapping utilities for API <-> Frontend conversions
 */
export const TypeMappers = {
  // Campaign Type mappings
  apiTypeToFrontend: (apiType: CampaignApiType): CampaignExperimentalType => {
    const mapping: Record<CampaignApiType, CampaignExperimentalType> = {
      [CampaignApiType.ONE_TIME]: CampaignExperimentalType.ONE_TIME,
      [CampaignApiType.RECURRING]: CampaignExperimentalType.RECURRING,
    };
    return mapping[apiType] || CampaignExperimentalType.ONE_TIME;
  },

  frontendTypeToApi: (frontendType: CampaignExperimentalType): string => {
    const mapping: Record<CampaignExperimentalType, string> = {
      [CampaignExperimentalType.ONE_TIME]: 'ONE_TIME',
      [CampaignExperimentalType.RECURRING]: 'RECURRING',
    };
    return mapping[frontendType] || 'ONE_TIME';
  },

  // Assignment Type mappings
  audienceTypeToAssignmentType: (audienceType: CampaignAudienceType): AssignmentType => {
    const mapping: Record<CampaignAudienceType, AssignmentType> = {
      [CampaignAudienceType.ALL]: AssignmentType.ALL_MEMBERS,
      [CampaignAudienceType.SEGMENT]: AssignmentType.SEGMENTS,
      [CampaignAudienceType.CIRCLE]: AssignmentType.CIRCLES,
    };
    return mapping[audienceType] || AssignmentType.ALL_MEMBERS;
  },

  assignmentTypeToAudience: (assignmentType: AssignmentType): string => {
    const mapping: Record<AssignmentType, string> = {
      [AssignmentType.ALL_MEMBERS]: 'ALL',
      [AssignmentType.SEGMENTS]: 'SEGMENT',
      [AssignmentType.CIRCLES]: 'CIRCLE',
    };
    return mapping[assignmentType] || 'ALL';
  },

  // Event Type mappings
  eventTypeToTriggerEvent: (eventType: CampaignEventType): AutomationTriggerEvent => {
    const mapping: Record<CampaignEventType, AutomationTriggerEvent> = {
      [CampaignEventType.BIRTHDAY]: AutomationTriggerEvent.BIRTHDAY,
      [CampaignEventType.CIRCLE_UPGRADE]: AutomationTriggerEvent.TIER_UPGRADE,
      [CampaignEventType.EMAIL_OPT_IN]: AutomationTriggerEvent.FIRST_PURCHASE,
      [CampaignEventType.REGISTRATION]: AutomationTriggerEvent.FIRST_PURCHASE,
    };
    return mapping[eventType] || AutomationTriggerEvent.BIRTHDAY;
  },

  triggerEventToApiEvent: (triggerEvent: AutomationTriggerEvent): string => {
    const mapping: Record<AutomationTriggerEvent, string> = {
      [AutomationTriggerEvent.BIRTHDAY]: 'BIRTHDAY',
      [AutomationTriggerEvent.TIER_UPGRADE]: 'CIRCLE_UPGRADE',
      [AutomationTriggerEvent.FIRST_PURCHASE]: 'EMAIL_OPT_IN',
      [AutomationTriggerEvent.PURCHASE_MILESTONE]: 'EMAIL_OPT_IN',
      [AutomationTriggerEvent.POINTS_MILESTONE]: 'EMAIL_OPT_IN',
      [AutomationTriggerEvent.ANNIVERSARY]: 'EMAIL_OPT_IN',
      [AutomationTriggerEvent.INACTIVITY_PERIOD]: 'EMAIL_OPT_IN',
    };
    return mapping[triggerEvent] || 'EMAIL_OPT_IN';
  },

  // Notification mappings
  notificationPlanToChannels: (plan: CampaignNotificationPlan): NotificationChannel[] => {
    const mapping: Record<CampaignNotificationPlan, NotificationChannel[]> = {
      [CampaignNotificationPlan.EMAIL]: [NotificationChannel.EMAIL],
      [CampaignNotificationPlan.PUSH]: [NotificationChannel.PUSH_NOTIFICATION],
      [CampaignNotificationPlan.BOTH]: [NotificationChannel.EMAIL, NotificationChannel.PUSH_NOTIFICATION],
    };
    return mapping[plan] || [NotificationChannel.EMAIL];
  },

  channelsToNotificationPlan: (channels: NotificationChannel[]): string => {
    const hasEmail = channels.includes(NotificationChannel.EMAIL);
    const hasPush = channels.includes(NotificationChannel.PUSH_NOTIFICATION);
    
    if (hasEmail && hasPush) return 'BOTH';
    if (hasPush) return 'PUSH';
    return 'EMAIL'; // Default fallback
  },
};

/**
 * Status inference logic
 */
export const StatusUtils = {
  inferStatus: (apiCampaign: {
    type: CampaignApiType;
    startDate: string;
    endDate?: string;
    executionTime?: string;
  }): CampaignExperimentalStatus => {
    const now = new Date();

    if (apiCampaign.type === CampaignApiType.ONE_TIME) {
      const executionDate = new Date(apiCampaign.executionTime || apiCampaign.startDate);
      return executionDate > now 
        ? CampaignExperimentalStatus.SCHEDULED 
        : CampaignExperimentalStatus.ENDED;
    }
    
    if (apiCampaign.type === CampaignApiType.RECURRING) {
      const startDate = new Date(apiCampaign.startDate);
      const endDate = apiCampaign.endDate ? new Date(apiCampaign.endDate) : null;
      
      if (now < startDate) return CampaignExperimentalStatus.SCHEDULED;
      if (endDate && now > endDate) return CampaignExperimentalStatus.ENDED;
      if (now >= startDate && (!endDate || now <= endDate)) return CampaignExperimentalStatus.ACTIVE;
    }
    
    // Default fallback
    const startDate = new Date(apiCampaign.startDate);
    return startDate > now 
      ? CampaignExperimentalStatus.SCHEDULED 
      : CampaignExperimentalStatus.ACTIVE;
  },
};

/**
 * Data validation utilities
 */
export const ValidationUtils = {
  isValidDate: (dateString: string): boolean => {
    const date = new Date(dateString);
    return !isNaN(date.getTime());
  },

  isValidTimeString: (timeString: string): boolean => {
    return /^([0-1]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/.test(timeString);
  },

  sanitizeTargets: (targets: string[] | undefined): string[] => {
    return targets?.filter(target => target?.trim()) || [];
  },
};
